import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import TicketDetailView from '../TicketDetailView';
import AssignTicketToAgentList from './componentes/AssignTicketToAgentList';
import AppContext from 'context/Context';
import {
    TicketsFilterTabs,
    TicketCardItem,
    TicketsEmptyState,
    TicketsLoadingState,
    AgentFilterSelect,
    EtiquetaFilterSelect,
    NivelFilterSelect,
    TicketsPagination
} from './componentes';
import { useTicketsList } from './hooks/useTicketsList';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faTimes,
    faSlidersH,
    faUserCheck,
    faTags,
    faLayerGroup
} from '@fortawesome/free-solid-svg-icons';

const TicketsView = ({ onTicketSelect, selectedTicket, setSelectedTicket, onTicketDetailLoaded, onInitialReady }) => {
    const [internalSelectedTicket, setInternalSelectedTicket] = useState(null);
    const { config: { isDark } = {} } = useContext(AppContext);
    const [filterTab, setFilterTab] = useState('en_progreso');
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [agents, setAgents] = useState([]);
    const [etiquetas, setEtiquetas] = useState([]);
    const [selectedEtiquetaId, setSelectedEtiquetaId] = useState('');
    const [selectedNivelId, setSelectedNivelId] = useState('');
    const [isLoadingEtiquetas, setIsLoadingEtiquetas] = useState(false);
    const { token, nombreCompleto, idUsuario } = useToken();
    const [selectedAgentId, setSelectedAgentId] = useState(idUsuario ? String(idUsuario) : '');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const hasStartedLoadingRef = useRef(false);
    const hasAutoCheckedTabRef = useRef(false);
    const hasInitializedAgentRef = useRef(false);
    const etiquetasLoadedRef = useRef(false);
    const { showSuccessToast, showErrorToast } = useToast();

    // Sincronizar selectedAgentId por defecto con el asesor logueado al iniciar
    useEffect(() => {
        if (idUsuario && !hasInitializedAgentRef.current) {
            hasInitializedAgentRef.current = true;
            setSelectedAgentId(String(idUsuario));
        }
    }, [idUsuario]);

    // Debounce de la búsqueda para optimizar peticiones
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Estados para el Modal de Asignación de Ticket
    const [ticketToAssign, setTicketToAssign] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);

    const currentTicket = selectedTicket !== undefined ? selectedTicket : internalSelectedTicket;
    const isViewingDetail = Boolean(currentTicket);

    // Solo consultar la lista de tickets a la BD si NO estamos dentro del detalle de un ticket
    const {
        tickets,
        counts,
        isLoading,
        page,
        setPage,
        pageSize,
        setPageSize,
        totalRegistros,
        totalPages,
        reloadTickets,
        markTicketAsRead
    } = useTicketsList(
        filterTab,
        selectedAgentId,
        !isViewingDetail,
        20,
        onInitialReady,
        null,
        null,
        debouncedSearchTerm,
        selectedEtiquetaId,
        selectedNivelId
    );

    // Si no hay tickets en progreso al terminar la primera carga, cambiar a pendientes por defecto
    useEffect(() => {
        if (isLoading) {
            hasStartedLoadingRef.current = true;
        }
        if (hasStartedLoadingRef.current && !isLoading && !hasAutoCheckedTabRef.current) {
            if (counts && counts.enProgreso === 0) {
                setFilterTab('pendientes');
            }
            hasAutoCheckedTabRef.current = true;
        }
    }, [isLoading, counts]);

    // Cargar catálogo de incidencias/etiquetas activas
    const fetchEtiquetas = useCallback(async () => {
        if (!token || etiquetasLoadedRef.current) return;
        setIsLoadingEtiquetas(true);
        try {
            const resp = await fetch('/api/SupportEngine/GetEtiquetas', {
                headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
            });
            if (resp.ok) {
                const result = await resp.json();
                const data = result.data || (Array.isArray(result) ? result : []);
                setEtiquetas(Array.isArray(data) ? data : []);
                etiquetasLoadedRef.current = true;
            }
        } catch {
            // Silencioso
        } finally {
            setIsLoadingEtiquetas(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEtiquetas();
    }, [fetchEtiquetas]);

    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const isFetchingAgentsRef = useRef(false);
    const agentsLoadedRef = useRef(false);

    // Cargar lista de agentes activos
    const fetchAgents = useCallback(async () => {
        if (!token || isFetchingAgentsRef.current || agentsLoadedRef.current) return;
        isFetchingAgentsRef.current = true;
        setIsLoadingAgents(true);
        try {
            const response = await fetch('/api/SupportEngine/GetAgentsList', {
                headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
            });
            if (response.ok) {
                const result = await response.json();
                const data = result.data || (Array.isArray(result) ? result : []);
                setAgents(Array.isArray(data) ? data : []);
                agentsLoadedRef.current = true;
            }
        } catch {
            // Fallback silencioso
        } finally {
            isFetchingAgentsRef.current = false;
            setIsLoadingAgents(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    // Abre el modal de asignación y asegura que la lista de asesores esté cargada
    const handleOpenAssignModal = (ticket) => {
        if (agents.length === 0) {
            fetchAgents();
        }
        setTicketToAssign(ticket);
        setShowAssignModal(true);
    };

    // Ejecuta la asignación cuando el usuario confirma en el modal
    const handleConfirmAssignTicket = async (ticket, selectedAgent) => {
        try {
            const targetAgent = selectedAgent || {
                idUsuario: Number(idUsuario),
                vNombreCompleto: nombreCompleto || 'Asesor'
            };

            const payload = {
                idTicket: ticket.idTicket,
                idUsuarioAsignado: Number(targetAgent.idUsuario),
                vUpdatedUser: targetAgent.vNombreCompleto || nombreCompleto || 'Asesor'
            };

            const response = await fetch('/api/SupportEngine/AssignTicketToAgent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + token
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Error al asignar el ticket');
            }

            if (showSuccessToast) {
                showSuccessToast(
                    'Asignación exitosa',
                    `Ticket #${ticket.idTicket} asignado a ${targetAgent.vNombreCompleto}. Saludo de bienvenida enviado.`
                );
            }

            reloadTickets();
        } catch (err) {
            if (showErrorToast) {
                showErrorToast('Error de asignación', err.message);
            }
        }
    };

    const handleSelectTicket = ticket => {
        if (!ticket) return;
        const ticketId = ticket.idTicket || ticket.id;
        if (markTicketAsRead) {
            markTicketAsRead(ticketId);
        }
        window.open(`/support-engine/ticket/${ticketId}`, '_blank');
    };

    const handleBackToList = () => {
        const currentTicketId = currentTicket?.idTicket || currentTicket?.id;
        if (currentTicketId && markTicketAsRead) {
            markTicketAsRead(currentTicketId);
        }
        if (setSelectedTicket) {
            setSelectedTicket(null);
        } else {
            setInternalSelectedTicket(null);
        }
        if (onTicketSelect) {
            onTicketSelect(null);
        }
    };

    // Contador de filtros activos en móvil
    const activeFiltersCount = (selectedAgentId ? 1 : 0) + (selectedEtiquetaId ? 1 : 0) + (selectedNivelId ? 1 : 0);

    if (currentTicket) {
        return (
            <TicketDetailView
                ticket={currentTicket}
                onBack={handleBackToList}
                onTicketUpdated={reloadTickets}
                onTicketDetailLoaded={(data) => {
                    if (onTicketDetailLoaded) onTicketDetailLoaded(data);
                    if (onInitialReady) onInitialReady();
                }}
                onSelectTicket={handleSelectTicket}
            />
        );
    }

    return (
        <div className="tickets-view-container">
            {/* 1. Vista Escritorio (Barra de filtros tradicional) */}
            <div
                className="d-none d-md-block rounded-3 p-3 mb-3 border"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
            >
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <TicketsFilterTabs
                        filterTab={filterTab}
                        setFilterTab={setFilterTab}
                        counts={counts}
                        isMobile={false}
                    />

                    <div className="d-flex align-items-center justify-content-end gap-2 gap-lg-3 flex-wrap flex-grow-1">
                        {/* Buscador de escritorio */}
                        <div className="position-relative" style={{ minWidth: '200px', maxWidth: '280px', flex: '1 1 auto' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm ps-4 pe-4 border shadow-none"
                                placeholder="Buscar por celular o # ticket..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                                    borderColor: isDark ? '#233142' : '#e2e8f0',
                                    color: isDark ? '#f8fafc' : '#334155',
                                    borderRadius: '8px',
                                    height: '34px',
                                    fontSize: '0.82rem'
                                }}
                            />
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="position-absolute text-muted"
                                style={{
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '0.78rem',
                                    pointerEvents: 'none'
                                }}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm('')}
                                    className="btn btn-link position-absolute p-0 text-muted border-0 shadow-none"
                                    style={{
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '0.75rem'
                                    }}
                                    title="Limpiar búsqueda"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>

                        {/* Filtros Dropdowns clásicos */}
                        <AgentFilterSelect
                            selectedAgentId={selectedAgentId}
                            onSelectAgent={setSelectedAgentId}
                            agents={agents}
                            currentUserId={idUsuario}
                            currentUserName={nombreCompleto}
                            isLoadingAgents={isLoadingAgents}
                        />

                        <EtiquetaFilterSelect
                            selectedEtiquetaId={selectedEtiquetaId}
                            onSelectEtiqueta={setSelectedEtiquetaId}
                            etiquetas={etiquetas}
                            isLoading={isLoadingEtiquetas}
                        />

                        <NivelFilterSelect
                            selectedNivelId={selectedNivelId}
                            onSelectNivel={setSelectedNivelId}
                        />
                    </div>
                </div>
            </div>

            {/* 2. Vista Móvil (Pestañas completas + Buscador y Botón Filtros Bottom Sheet) */}
            <div className="d-md-none mb-3 d-flex flex-column gap-2">
                <TicketsFilterTabs
                    filterTab={filterTab}
                    setFilterTab={setFilterTab}
                    counts={counts}
                    isMobile={true}
                />

                <div className="d-flex align-items-center gap-2 w-100">
                    <div className="position-relative flex-grow-1" style={{ minWidth: 0 }}>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-4 pe-4 rounded-pill shadow-none"
                            placeholder="Buscar por celular o # ticket..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                borderColor: isDark ? '#233142' : '#e2e8f0',
                                color: isDark ? '#f8fafc' : '#334155',
                                height: '38px',
                                fontSize: '0.82rem'
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="position-absolute text-muted"
                            style={{
                                left: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '0.78rem',
                                pointerEvents: 'none'
                            }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="btn btn-link position-absolute p-0 text-muted border-0 shadow-none"
                                style={{
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '0.75rem'
                                }}
                                title="Limpiar búsqueda"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowBottomSheet(true)}
                        className="btn btn-sm px-3 rounded-pill d-inline-flex align-items-center gap-2 shadow-none flex-shrink-0"
                        style={{
                            height: '38px',
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                            color: isDark ? '#cbd5e1' : '#334155',
                            fontSize: '0.82rem',
                            fontWeight: 600
                        }}
                        title="Abrir Filtros"
                    >
                        <FontAwesomeIcon icon={faSlidersH} style={{ fontSize: '0.78rem', color: '#0284c7' }} />
                        <span>Filtros</span>
                       
                    </button>
                </div>
            </div>

            {showBottomSheet && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end justify-content-center"
                    style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(3px)',
                        zIndex: 1080,
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                    onClick={() => setShowBottomSheet(false)}
                >
                    <div
                        className="w-100 p-3 rounded-top-4 shadow-lg border-top"
                        style={{
                            maxWidth: '500px',
                            backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                            borderColor: isDark ? '#334155' : '#e2e8f0',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Indicador superior de arrastre */}
                        <div className="d-flex justify-content-center mb-2">
                            <div
                                style={{
                                    width: '38px',
                                    height: '4px',
                                    borderRadius: '2px',
                                    backgroundColor: isDark ? '#475569' : '#cbd5e1'
                                }}
                            />
                        </div>

                        {/* Encabezado del Bottom Sheet */}
                        <div
                            className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom"
                            style={{ borderColor: isDark ? '#1e293b' : '#e2e8f0' }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faSlidersH} style={{ color: '#0284c7', fontSize: '0.9rem' }} />
                                <h6 className="fw-bold mb-0" style={{ color: isDark ? '#f8fafc' : '#0f172a', fontSize: '0.95rem' }}>
                                    Filtros de Búsqueda
                                </h6>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowBottomSheet(false)}
                                className="btn btn-sm p-1 rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
                                    color: isDark ? '#cbd5e1' : '#64748b'
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.82rem' }} />
                            </button>
                        </div>

                        {/* Cuerpo con lista de Filtros */}
                        <div className="d-flex flex-column gap-3 mb-2">
                            {/* 1. Asesor asignado */}
                            <div>
                                <label
                                    className="form-label mb-1 fw-medium d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    <FontAwesomeIcon icon={faUserCheck} style={{ fontSize: '0.74rem', color: '#0284c7' }} />
                                    <span>Asesor asignado</span>
                                </label>
                                <AgentFilterSelect
                                    variant="chip"
                                    className="w-100"
                                    hideIcon={true}
                                    selectedAgentId={selectedAgentId}
                                    onSelectAgent={setSelectedAgentId}
                                    agents={agents}
                                    currentUserId={idUsuario}
                                    currentUserName={nombreCompleto}
                                    isLoadingAgents={isLoadingAgents}
                                />
                            </div>

                            {/* 2. Incidencia / Etiqueta */}
                            <div>
                                <label
                                    className="form-label mb-1 fw-medium d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    <FontAwesomeIcon icon={faTags} style={{ fontSize: '0.74rem', color: '#0284c7' }} />
                                    <span>Tipo de incidencia</span>
                                </label>
                                <EtiquetaFilterSelect
                                    variant="chip"
                                    className="w-100"
                                    hideIcon={true}
                                    showIcon={false}
                                    selectedEtiquetaId={selectedEtiquetaId}
                                    onSelectEtiqueta={setSelectedEtiquetaId}
                                    etiquetas={etiquetas}
                                    isLoading={isLoadingEtiquetas}
                                />
                            </div>

                            {/* 3. Nivel de Soporte */}
                            <div>
                                <label
                                    className="form-label mb-1 fw-medium d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '0.74rem', color: '#0284c7' }} />
                                    <span>Nivel de soporte</span>
                                </label>
                                <NivelFilterSelect
                                    variant="chip"
                                    className="w-100"
                                    hideIcon={true}
                                    showIcon={false}
                                    selectedNivelId={selectedNivelId}
                                    onSelectNivel={setSelectedNivelId}
                                />
                            </div>
                        </div>

                        {/* Botón de confirmación */}
                        <div className="mt-4">
                            <button
                                type="button"
                                className="btn btn-primary w-100 py-2 fw-semibold shadow-none"
                                style={{ borderRadius: '10px', fontSize: '0.88rem' }}
                                onClick={() => setShowBottomSheet(false)}
                            >
                                Ver resultados
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ticket List Cards / Loader / Empty State / Pagination */}
            {isLoading && tickets.length === 0 ? (
                <TicketsLoadingState />
            ) : tickets.length === 0 ? (
                <TicketsEmptyState filterTab={filterTab} hasSearch={Boolean(debouncedSearchTerm)} onReload={reloadTickets} />
            ) : (
                <>
                    <div className="d-flex flex-column gap-3">
                        {tickets.map(ticket => (
                            <TicketCardItem
                                key={ticket.idTicket}
                                ticket={ticket}
                                agents={agents}
                                isLoadingAgents={isLoadingAgents}
                                onRequestAgents={fetchAgents}
                                onSelectTicket={handleSelectTicket}
                                onAssignTicketAgent={handleOpenAssignModal}
                            />
                        ))}
                    </div>

                    <TicketsPagination
                        currentPage={page}
                        totalPages={totalPages}
                        totalRecords={totalRegistros}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={size => {
                            setPageSize(size);
                            setPage(1);
                        }}
                        isLoading={isLoading}
                    />
                </>
            )}

            {/* MODAL DE ASIGNACIÓN DE ASESOR */}
            <AssignTicketToAgentList
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                ticket={ticketToAssign}
                agents={agents}
                currentUserId={idUsuario}
                currentUserName={nombreCompleto}
                onConfirm={handleConfirmAssignTicket}
            />
        </div>
    );
};

export default TicketsView;